import React, { useEffect, useMemo } from 'react';
import { Grid } from '@material-ui/core';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';

import { getCampaigns, cancelCampaign, setCampaignsPage, setCampaignsPageItems } from '../../../store/messaging/actions';
import Widget from '../../../components/Widget/Widget';
import Table from '../../../components/Table';
import { ROUTES, NAMES, IDS } from '../../../constants/routes';
import Breadcrumbs from '../../../components/Breadcrumbs';

const breadcrumsRoutes = [
  {
    route: ROUTES[IDS.MESSAGING],
    name: NAMES[IDS.MESSAGING],
  },
];

const headCells = [
  { id: 'campaignId', numeric: false, disablePadding: true, label: 'Campaign' },
  { id: 'title', numeric: false, disablePadding: false, label: 'Title' },
  { id: 'status', numeric: false, disablePadding: false, label: 'Status' },
  { id: 'localSendAt', numeric: false, disablePadding: false, label: 'Send At (user local)' },
  { id: 'audience', numeric: true, disablePadding: false, label: 'Audience' },
  { id: 'sent', numeric: true, disablePadding: false, label: 'Sent' },
  { id: 'failed', numeric: true, disablePadding: false, label: 'Failed' },
  { id: 'skipped', numeric: true, disablePadding: false, label: 'Skipped' },
  { id: 'createdAt', numeric: false, disablePadding: false, label: 'Created At' },
];

const ACTIVE = ['scheduled', 'running'];

const Campaigns = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const selectedProjectId = useSelector((state) => state.projects.selectedProjectId);
  const { data, count, pending, pageNumber, pageItemCount } = useSelector((state) => state.messaging.campaigns);

  const tableData = useMemo(
    () =>
      data.map((c) => ({
        campaignId: c.campaignId,
        title: c.title.length > 30 ? `${c.title.slice(0, 30)}...` : c.title,
        status: c.status,
        // Stored as wall-clock digits in UTC, so format in UTC to show the digits the user picked.
        localSendAt: moment.utc(c.localSendAt).format('DD.MM.YY HH:mm'),
        audience: c.result?.audience ?? 0,
        sent: c.result?.sent ?? 0,
        failed: c.result?.failed ?? 0,
        skipped: c.result?.skipped ?? 0,
        createdAt: c.createdAt,
      })),
    [data],
  );

  useEffect(() => {
    dispatch(setCampaignsPage(1));
  }, [selectedProjectId]);

  useEffect(() => {
    dispatch(getCampaigns());
  }, [selectedProjectId, pageNumber, pageItemCount]);

  const handleChangePage = (event, newPage) => {
    dispatch(setCampaignsPage(newPage + 1));
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch(setCampaignsPageItems(parseInt(event.target.value, 10)));
    dispatch(setCampaignsPage(1));
  };

  // Rows carry no _id (the table renders every key), so the campaign is found by its id in the same order.
  const handleRowClick = async (row, index) => {
    const campaign = data[index];
    if (!campaign || !ACTIVE.includes(campaign.status)) return;
    if (!window.confirm(`Cancel campaign "${campaign.campaignId}"?`)) return;
    try {
      await dispatch(cancelCampaign(campaign._id));
    } catch (error) {
      alert(error?.response?.data?.message || 'Cancel failed');
    }
  };

  return (
    <>
      <Grid container xs={12}>
        <Grid item xs={10}>
          <Breadcrumbs links={breadcrumsRoutes} currentRoute={NAMES[IDS.CAMPAIGNS]} />
        </Grid>
        <Grid item xs={2} className={classes.refresh}>
          <Button variant="outlined" color="primary" onClick={() => dispatch(getCampaigns())}>
            Refresh
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={4} className={classes.main}>
        <Grid item xs={12}>
          <Widget disableWidgetMenu noBodyPadding noHeaderPadding bodyClass={classes.tableTopOverflow}>
            <Grid item xs={12}>
              <Table
                headCells={headCells}
                rows={tableData}
                count={count}
                order="asc"
                orderBy="createdAt"
                page={pageNumber - 1}
                rowsPerPage={pageItemCount}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                onRowClick={handleRowClick}
              />
              {pending && (
                <div className={classes.loaderWrap}>
                  <div className={classes.loader}>
                    <CircularProgress size={26} />
                  </div>
                </div>
              )}
            </Grid>
          </Widget>
        </Grid>
      </Grid>
    </>
  );
};

export default Campaigns;

const useStyles = makeStyles((theme) => ({
  main: {
    marginTop: theme.spacing(2),
  },
  refresh: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  tableTopOverflow: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  loaderWrap: {
    width: '100%',
    height: '100%',
    backgroundColor: theme.palette.background.overlay,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9,
  },
  loader: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
}));
